import { useContent } from "contexts/contentContext/contentContext";
import styles from "./advantages.module.scss";
import Icon from "components/Elements/Icon/Icon";
import { memo, useEffect, useRef, useState, useCallback } from "react";
import throttle from "lodash/throttle";

const SEGMENTS_COUNT = 5; // Number of snake segments
const MOBILE_BREAKPOINT = 769;
const THROTTLE_DELAY = 16; // ~60fps

const Advantages = memo(() => {
  const { getTextByKey } = useContent();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const positions = useRef<Array<{ x: number; y: number }>>([]);
  const [segments, setSegments] = useState<Array<{ x: number; y: number }>>(
    Array(SEGMENTS_COUNT).fill({ x: 0, y: 0 })
  );
  const [isMobile, setIsMobile] = useState(false);
  const animationFrame = useRef<number>();

  // Оптимизированная функция обновления сегментов
  const updateSegments = useCallback(() => {
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }

    animationFrame.current = requestAnimationFrame(() => {
      setSegments(
        positions.current.map((pos, index) => ({
          x: pos.x,
          y: pos.y,
        }))
      );
    });
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    // Check when mounting
    checkMobile();

    // Add a window resize listener
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  useEffect(() => {
    if (isMobile) return; // Do not add listeners on mobile devices

    const handleMouseMove = throttle(
      (e: MouseEvent) => {
        if (!sectionRef.current) return;

        const rect = sectionRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Add a new position to the beginning of the array
        positions.current.unshift({ x, y });
        // Limit the number of saved positions
        if (positions.current.length > SEGMENTS_COUNT) {
          positions.current = positions.current.slice(0, SEGMENTS_COUNT);
        }

        updateSegments();
      },
      THROTTLE_DELAY,
      { leading: true, trailing: true }
    );

    const handleMouseEnter = () => {
      setIsHovering(true);
      positions.current = [];
    };

    const handleMouseLeave = () => {
      setIsHovering(false);
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
    };

    const section = sectionRef.current;
    if (section) {
      section.addEventListener("mousemove", handleMouseMove);
      section.addEventListener("mouseenter", handleMouseEnter);
      section.addEventListener("mouseleave", handleMouseLeave);
    }

    return () => {
      if (section) {
        section.removeEventListener("mousemove", handleMouseMove);
        section.removeEventListener("mouseenter", handleMouseEnter);
        section.removeEventListener("mouseleave", handleMouseLeave);
        handleMouseMove.cancel();
      }
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
    };
  }, [isMobile, updateSegments]);

  const advantageList = [
    {
      icon: "advertising-page-exposure",
      title: `${getTextByKey("exclusive-exposure-title")}`,
      description: `${getTextByKey("exclusive-exposure-description")}`,
    },
    {
      icon: "advertising-page-support",
      title: `${getTextByKey("creative-support-title")}`,
      description: `${getTextByKey("creative-support-description")}`,
    },
    {
      icon: "advertising-page-audience",
      title: `${getTextByKey("high-quality-audience-title")}`,
      description: `${getTextByKey("high-quality-audience-description")}`,
    },
    {
      icon: "advertising-page-bypass",
      title: `${getTextByKey("ad-blocker-bypass-title")}`,
      description: `${getTextByKey("ad-blocker-bypass-description")}`,
    },
    {
      icon: "advertising-page-performance",
      title: `${getTextByKey("performance-optimization-title")}`,
      description: `${getTextByKey("performance-optimization-description")}`,
    },
  ];

  const parseTitle = (text: string) => {
    const parts = text.split(/<span>|<\/span>/);
    return parts.map((part, index) => {
      // If the index is odd, it is text inside a <span>
      if (index % 2 === 1) {
        return <span key={index}>{part}</span>;
      }
      return part;
    });
  };

  return (
    <div className={styles.whyJmailSection} ref={sectionRef}>
      {!isMobile &&
        segments.map((segment, index) => (
          <div
            key={index}
            className={`${styles.mouseFollower} ${
              isHovering ? styles.visible : ""
            }`}
            style={{
              left: `${segment.x}px`,
              top: `${segment.y}px`,
              opacity: isHovering ? 1 - index * (1 / SEGMENTS_COUNT) : 0,
              transform: `translate(-50%, -50%) scale(${
                1 - index * (0.15 / SEGMENTS_COUNT)
              })`,
              willChange: "transform, opacity",
            }}
          />
        ))}
      <div className={styles.whyJmailSectionContent}>
        <h2>{parseTitle(getTextByKey("why-jmail-title"))}</h2>
        <ul className={styles.advantagesGrid}>
          {advantageList.map((item, index) => (
            <li key={index} className={styles.advantageItem}>
              <div className={styles.iconWrapper}>
                <Icon name={item.icon} />
              </div>
              <div className={styles.advantageItemContent}>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
});

export default Advantages;
